<template>
  <v-container v-if="enabled" class="my-10">
    <v-carousel v-if="images.length"
      v-model="currentSlide"
      :height="carouselHeight"
      :hide-delimiters="hideDelimiters"
      :delimiter-icon="delimiterIcon"
      :show-arrows="showArrows"
      :cycle="autoplay"
      :interval="intervalMs"
    >
      <v-carousel-item
        v-for="(group, groupIndex) in groupedImages"
        :key="groupIndex"
      >
        <v-row no-gutters class="fill-height">
          <v-col
            v-for="(image, imageIndex) in group"
            :key="`${groupIndex}-${imageIndex}`"
            :cols="columnSize"
            class="fill-height"
          >
            <v-img
              :src="image.src"
              :alt="image.alt || ''"
              height="100%"
              width="100%"
              cover
            />
          </v-col>
        </v-row>
      </v-carousel-item>
    </v-carousel>

    <div v-else class="empty-carousel" :style="{height: `${carouselHeight}px`}"></div>

    <div v-if="images.length && usesCustomDelimiters" class="text-center mt-2">
      <v-btn
        v-for="(_, index) in groupedImages"
        :key="index"
        icon
        small
        class="mx-1"
        :color="currentSlide === index ? 'primary' : 'grey lighten-1'"
        @click="currentSlide = index"
      >
        <v-icon small>mdi-circle</v-icon>
      </v-btn>
    </div>
  </v-container>
</template>

<script>
export default {
  name: "ConfigurableCarousel",

  props: {
    section: {
      type: Object,
      default: null,
    },
    defaultImages: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      currentSlide: 0,
    };
  },

  computed: {
    content() {
      return this.section?.content || {};
    },

    styles() {
      return this.section?.styles || {};
    },

    enabled() {
      return this.section ? this.section.enabled !== false : true;
    },

    images() {
      const configured = Array.isArray(this.content.images)
        ? this.content.images
            .map((image) => ({
              src: image.url || image.src || "",
              alt: image.alt || "",
            }))
            .filter((image) => image.src)
        : [];

      if (configured.length) return configured;

      return this.defaultImages.map((image) => (
        typeof image === "string" ? { src: image, alt: "" } : image
      ));
    },

    autoplay() {
      if (typeof this.content.autoplay === "boolean") return this.content.autoplay;
      return this.variant !== 1;
    },

    intervalMs() {
      const fallback = this.variant === 2 ? 2 : this.variant === 3 ? 1 : 4;
      const seconds = Number(this.content.interval_seconds || fallback);
      return Math.max(1, Math.min(60, seconds)) * 1000;
    },

    itemsPerSlide() {
      let configured;

      if (this.$vuetify.breakpoint.smAndDown) {
        configured = this.styles.items_per_slide_mobile;
      } else if (this.$vuetify.breakpoint.mdOnly) {
        configured = this.styles.items_per_slide_tablet;
      } else {
        configured = this.styles.items_per_slide_desktop;
      }

      return Math.max(1, Math.min(4, Number(configured || 1)));
    },

    columnSize() {
      return 12 / this.itemsPerSlide;
    },

    groupedImages() {
      const groups = [];
      for (let i = 0; i < this.images.length; i += this.itemsPerSlide) {
        groups.push(this.images.slice(i, i + this.itemsPerSlide));
      }
      return groups;
    },

    carouselHeight() {
      const value = this.$vuetify.breakpoint.smAndDown
        ? this.styles.height_mobile
        : this.styles.height_desktop;
      return Number(value || (this.$vuetify.breakpoint.smAndDown ? 280 : 400));
    },

    hideDelimiters() {
      return this.variant !== 2;
    },

    delimiterIcon() {
      return this.variant === 2 ? "mdi-circle-outline" : undefined;
    },

    showArrows() {
      if (this.variant === 2) return false;
      return true;
    },

    usesCustomDelimiters() {
      return this.variant === 3;
    },
  },

  watch: {
    groupedImages() {
      if (this.currentSlide >= this.groupedImages.length) {
        this.currentSlide = 0;
      }
    },
  },
};
</script>

<style scoped>.empty-carousel{width:100%;border-radius:var(--site-radius,16px);background:#e5e7eb}</style>
